import {
  Planzone,
  Project,
  secondaryCriteria,
  SecondaryCriteriaElementType,
  SecondaryCriteriaTemplate,
} from "@planzone/dto";
import { HttpClient, HttpMethods } from "@planzone/services";

function sanitizeJsonString(jsonString: string): string {
  return jsonString.replace(
    /"(?:[^"\\]|\\.)*"/g, // match string literals
    (str) =>
      str.replace(/\t/g, "\\t").replace(/\r/g, "\\r").replace(/\n/g, "\\n"),
  );
}

function parseProjectElementsSettings({
  data,
}: Planzone | Project): SecondaryCriteriaTemplate {
  return data.secondaryProjectElementsCriteriaTemplate
    ? JSON.parse(
        sanitizeJsonString(data.secondaryProjectElementsCriteriaTemplate),
      )
    : [];
}

function parseProjectsSettings({
  data,
}: Planzone | Project): SecondaryCriteriaTemplate {
  return data.secondaryProjectsCriteriaTemplate
    ? JSON.parse(sanitizeJsonString(data.secondaryProjectsCriteriaTemplate))
    : [];
}

function parseResourcesSettings({
  data,
}: Planzone | Project): SecondaryCriteriaTemplate {
  return data.secondaryResourcesCriteriaTemplate
    ? JSON.parse(sanitizeJsonString(data.secondaryResourcesCriteriaTemplate))
    : [];
}

function getProjectsSecondaryCriteriaTemplate(httpClient: HttpClient) {
  return (namespace: string, scope: "planzone" | "project") =>
    httpClient
      .sendHttpRequest<Planzone | Project>({
        endpoint: `/${scope === "planzone" ? "planzones" : "projects"}/${namespace}`,
        method: HttpMethods.GET,
      })
      .then(parseProjectsSettings);
}

function getResourcesSecondaryCriteriaTemplate(httpClient: HttpClient) {
  return (namespace: string, scope: "planzone" | "project") =>
    httpClient
      .sendHttpRequest<Planzone | Project>({
        endpoint: `/${scope === "planzone" ? "planzones" : "projects"}/${namespace}`,
        method: HttpMethods.GET,
      })
      .then(parseResourcesSettings);
}

function parseProjectReportsSettings({
  data,
}: Planzone | Project): SecondaryCriteriaTemplate {
  return data.secondaryProjectReportsCriteriaTemplate
    ? JSON.parse(
        sanitizeJsonString(data.secondaryProjectReportsCriteriaTemplate),
      )
    : [];
}

function parseTimeRecordsSettings({
  data,
}: Planzone | Project): SecondaryCriteriaTemplate {
  return data.secondaryTimeRecordsCriteriaTemplate
    ? JSON.parse(sanitizeJsonString(data.secondaryTimeRecordsCriteriaTemplate))
    : [];
}

function getProjectElementsSecondaryCriteriaTemplate(httpClient: HttpClient) {
  return (namespace: string, scope: "planzone" | "project") =>
    httpClient
      .sendHttpRequest<Planzone>({
        endpoint: `/${scope === "planzone" ? "planzones" : "projects"}/${namespace}`,
        method: HttpMethods.GET,
      })
      .then(parseProjectElementsSettings);
}

function getProjectReportsSecondaryCriteriaTemplate(httpClient: HttpClient) {
  return (namespace: string, scope: "planzone" | "project") =>
    httpClient
      .sendHttpRequest<Planzone | Project>({
        endpoint: `/${scope === "planzone" ? "planzones" : "projects"}/${namespace}`,
        method: HttpMethods.GET,
      })
      .then(parseProjectReportsSettings);
}

function getTimeRecordsSecondaryCriteriaTemplate(httpClient: HttpClient) {
  return (namespace: string, scope: "planzone" | "project") =>
    httpClient
      .sendHttpRequest<Planzone | Project>({
        endpoint: `/${scope === "planzone" ? "planzones" : "projects"}/${namespace}`,
        method: HttpMethods.GET,
      })
      .then(parseTimeRecordsSettings);
}

function getSecondaryCriteriaTemplate(httpClient: HttpClient) {
  return (
    namespace: string,
    scope: "planzone" | "project",
    elementType: SecondaryCriteriaElementType,
  ) =>
    (elementType === "project"
      ? getProjectsSecondaryCriteriaTemplate(httpClient)(namespace, scope)
      : elementType === "resource"
        ? getResourcesSecondaryCriteriaTemplate(httpClient)(namespace, scope)
        : elementType === "projectReport"
          ? getProjectReportsSecondaryCriteriaTemplate(httpClient)(
              namespace,
              scope,
            )
          : elementType === "timeRecord"
            ? getTimeRecordsSecondaryCriteriaTemplate(httpClient)(
                namespace,
                scope,
              )
            : getProjectElementsSecondaryCriteriaTemplate(httpClient)(
                namespace,
                scope,
              )
    ).then((template) =>
      template.map((criteria) => ({
        ...secondaryCriteria,
        ...criteria,
        options: criteria.options.map((option) => ({
          ...secondaryCriteria,
          ...option,
        })),
      })),
    );
}

export { getSecondaryCriteriaTemplate };
